import React, { Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { studentPersonaQueryKey, useStudentPersona } from "../queries";
import { Avatar, AvatarProps, FloatingPosition, HoverCard, Skeleton, Text, TextProps } from "@mantine/core";
import { StudentExtendedPersona } from "@features/extended-personas/components/StudentExtendedPersona";
import { QueryErrorBoundary } from "@components/errors/QueryErrorBoundary";
import { LoadingIndicator } from "@ui/LoadingIndicator";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import { usePortalTabs } from "@features/portal-tabs/usePortalTabs";
import { StudentSelectContextMenu } from "@features/portal-tabs/components/StudentSelectContextMenu";
import { getPortalsApiResourceUrl } from "@services/api/portalApi";

type PersonaProps = {
    id: string;
    studentNumberStyle?: 'none' | 'default' | 'dimmed';
    size?: number;
    textProps?: TextProps;
    avatarProps?: AvatarProps;
    reverseOrder?: boolean;
    containerProps?: React.HTMLAttributes<HTMLElement>;
    showPhoto?: boolean;
    clickable?: boolean;
    dropDownPosition?: FloatingPosition;
    hoverable?: boolean;
    withinPortal?: boolean;
};

const isNumberLike = (value: unknown): value is number | string => {
    if (typeof value === "number") {
        return true;
    }

    if (typeof value === "string") {
        if (value.startsWith("calc(") || value.startsWith("var(") || (value.includes(" ") && value.trim() !== "")) {
            return true;
        }

        const cssUnitsRegex = /^[+-]?[0-9]+(\.[0-9]+)?(px|em|rem|ex|ch|lh|rlh|vw|vh|vmin|vmax|vb|vi|svw|svh|lvw|lvh|dvw|dvh|cm|mm|in|pt|pc|q|cqw|cqh|cqi|cqb|cqmin|cqmax|%)?$/;
        const values = value.trim().split(/\s+/);
        return values.every((val) => cssUnitsRegex.test(val));
    }

    return false;
};

const avatarSizeMap: Record<"xs" | "sm" | "md" | "lg" | "xl", number> = {
    xs: 16,
    sm: 26,
    md: 38,
    lg: 56,
    xl: 84,
};

const toAvatarSize = (sizeValue: AvatarProps["size"] | undefined, fallback: number): number | string => {
    const resolved = sizeValue ?? fallback;
    if (typeof resolved === "undefined") {
        return fallback;
    }

    if (isNumberLike(resolved)) {
        return resolved;
    }

    return avatarSizeMap[resolved as keyof typeof avatarSizeMap] ?? `var(--avatar-size-${resolved})`;
};

const toTextSize = (sizeValue: TextProps["size"] | undefined): number | string => {
    const resolved = sizeValue ?? "sm";
    return isNumberLike(resolved) ? resolved : `var(--mantine-font-size-${resolved})`;
};

export function StudentPersona({ id, studentNumberStyle = 'default', size = 32, textProps, avatarProps, reverseOrder = false, containerProps, showPhoto = true, clickable = false, dropDownPosition = "top", hoverable = true, withinPortal = true }: PersonaProps) {
    const qc = useQueryClient();
    const { data, isLoading } = useStudentPersona(id);
    const { openStudent } = usePortalTabs();

    const viewMode = usePortalModeStore(state => state.viewMode);
    const canHover = hoverable === true && viewMode === 'staff'

    const [failedUrls, setFailedUrls] = React.useState<ReadonlySet<string>>(() => new Set());
    const refreshedUrlsRef = React.useRef(new Set<string>());
    const photoUrl = data?.photo?.url ?? null;
    // Derive the source directly: metadata arriving should not schedule a
    // second render just to copy its URL into component state.
    const imgSrc = photoUrl && !failedUrls.has(photoUrl) ? photoUrl : null;

    if (isLoading || !data) {
        const avatarSkeletonSize = toAvatarSize(avatarProps?.size, size);
        const textSkeletonHeight = toTextSize(textProps?.size);
        const textSkeletonWidth = typeof textSkeletonHeight === "number"
            ? textSkeletonHeight * 10
            : `calc(${textSkeletonHeight} * 10)`;

        return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Skeleton width={avatarSkeletonSize} height={avatarSkeletonSize} radius="xl" />
            <Skeleton width={textSkeletonWidth} height={textSkeletonHeight} radius={6} />
        </div>
        );
    }

    const { className, style, ...restContainerProps } = containerProps ?? {};
    const rootClassName = `${className ? `${className} ` : ""}shrink-0`;
    const rootStyle: React.CSSProperties = {
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyItems: "start",
        flexDirection: reverseOrder ? "row-reverse" : "row",
        flexWrap: "nowrap",
        width: "fit-content",
        ...style,
    };

    const persona = (
        <>
            {showPhoto && <Avatar 
                name={data.displayName} 
                color='dark' 
                size={avatarProps?.size ?? 'sm'}
                src={imgSrc ? getPortalsApiResourceUrl(imgSrc) : undefined}
                {...avatarProps}
                imageProps={{
                    ...avatarProps?.imageProps,
                    onError: (event) => {
                        avatarProps?.imageProps?.onError?.(event);
                        if (!photoUrl || refreshedUrlsRef.current.has(photoUrl)) return;

                        // Mantine forwards image errors through imageProps.
                        // Keep failed URLs suppressed and refresh each URL once.
                        refreshedUrlsRef.current.add(photoUrl);
                        setFailedUrls((current) => new Set([...current, photoUrl]));
                        void qc.invalidateQueries({ queryKey: studentPersonaQueryKey(id) });
                    },
                }}
            />}
            <Text fw={500} size="sm" {...textProps}>
                {data.displayName} {studentNumberStyle !== 'none' ? <Text span c={studentNumberStyle === 'dimmed' ? 'dimmed' : undefined}>({data.studentNumber})</Text> : null}
            </Text>
        </>
    );

    const targetContent = !clickable ? (
        <div className={rootClassName} style={rootStyle} {...restContainerProps}>
            {persona}
        </div>
    ) : (
        <a
            href={`/students/${encodeURIComponent(data.studentNumber)}`}
            className={rootClassName}
            style={{ ...rootStyle, color: "inherit", textDecoration: "none" }}
            onClick={(event) => {
                if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }

                event.preventDefault();
                openStudent(data.studentNumber);
            }}
            {...(restContainerProps as any)}
        >
            {persona}
        </a>
    );
    const target = clickable ? (
        <StudentSelectContextMenu studentNo={data.studentNumber}>
            {targetContent}
        </StudentSelectContextMenu>
    ) : targetContent;

    if (!canHover) {
        return target;
    }

    return (
        <HoverCard
            shadow="md"
            withArrow
            zIndex={1000}
            withinPortal={withinPortal}
            hideDetached={true}
            floatingStrategy="fixed"
            offset={0}
            closeDelay={50}
            position={dropDownPosition}
        >
            <HoverCard.Target>
                <div style={{ display: "inline-flex", width: "fit-content" }}>
                    {target}
                </div>
            </HoverCard.Target>
            <HoverCard.Dropdown onClick={(e) => {e.stopPropagation(); e.preventDefault()}}>
                <QueryErrorBoundary title="Failed to load student details" >
                    <Suspense fallback={<LoadingIndicator resource="student details" />}>
                        <StudentExtendedPersona studentNumber={data.studentNumber} />
                    </Suspense>
                </QueryErrorBoundary>
            </HoverCard.Dropdown>
        </HoverCard>
    );
}
